/**
 * Navigation — tree-shape sidebar driven by navigationConfig.
 *
 * Ported from QYAS Analytics, lucide icons + qfinpricer's MUI theme.
 * Two levels of expand/collapse (section → item → optional sub-items).
 *
 * Spec: 2026-05-19-qfinpricer-portal-skin-qyas.md (Day 1, item 1)
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import {
  navigationConfig,
  findSectionForPath,
  type NavigationItem,
  type NavigationSection,
} from './navigationConfig';
import { useWorkspaceTabs } from './workspaceTabsStore';
import { buildPageTab, isOpenInNewTabGesture } from './workspaceTabsHelpers';

interface NavigationProps {
  onNavigate?: () => void;
}

export const Navigation: React.FC<NavigationProps> = ({ onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const wsTabs = useWorkspaceTabs();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  // Auto-open the section that owns the active route on first mount and on route change.
  const activeSection = useMemo(
    () => findSectionForPath(location.pathname),
    [location.pathname],
  );
  useEffect(() => {
    if (activeSection) {
      setOpenSections((prev) =>
        prev[activeSection.id] ? prev : { ...prev, [activeSection.id]: true },
      );
    }
  }, [activeSection]);

  const handleSectionToggle = (sectionId: string) => {
    setOpenSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const handleItemToggle = (path: string) => {
    setOpenItems((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const handleNavigate = (path: string, e?: React.MouseEvent) => {
    // Cmd/Ctrl/middle-click on a leaf item opens it as a PAGE tab (pinned
    // to this exact path), instead of folding into the section tab.
    if (e && isOpenInNewTabGesture(e)) {
      e.preventDefault();
      wsTabs.open(buildPageTab(path));
      navigate(path);
      onNavigate?.();
      return;
    }
    navigate(path);
    onNavigate?.();
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const renderItems = (items: NavigationItem[], level = 0): React.ReactNode =>
    items.map((item) => {
      const hasChildren = !!item.children?.length;
      const isOpen = openItems[item.path] || false;
      const active = isActive(item.path);
      const indent = 4 + level * 2;

      if (hasChildren) {
        return (
          <React.Fragment key={item.path}>
            <ListItemButton
              sx={{ pl: indent }}
              onClick={() => handleItemToggle(item.path)}
              selected={active && !item.children!.some((c) => isActive(c.path))}
            >
              {item.icon && <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>}
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />
              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </ListItemButton>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderItems(item.children!, level + 1)}
              </List>
            </Collapse>
          </React.Fragment>
        );
      }

      return (
        <Tooltip
          key={item.path}
          title="Click to open · Cmd/Ctrl-click for new tab"
          placement="right"
          enterDelay={700}
        >
          <ListItemButton
            sx={{ pl: indent }}
            onClick={(e) => handleNavigate(item.path, e)}
            onAuxClick={(e) => {
              if (e.button === 1) handleNavigate(item.path, e);  // middle-click
            }}
            selected={active}
          >
            {item.icon && <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>}
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />
          </ListItemButton>
        </Tooltip>
      );
    });

  return (
    <List
      sx={{
        width: '100%',
        pt: 1,
        overflowY: 'auto',
        height: 'calc(100vh - 48px)', // 48px AppBar
      }}
    >
      {navigationConfig.map((section: NavigationSection) => (
        <React.Fragment key={section.id}>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleSectionToggle(section.id)}>
              <ListItemIcon sx={{ minWidth: 36 }}>{section.icon}</ListItemIcon>
              <ListItemText
                primary={section.label}
                primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
              />
              {openSections[section.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </ListItemButton>
          </ListItem>
          <Collapse in={openSections[section.id]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {renderItems(section.children)}
            </List>
          </Collapse>
        </React.Fragment>
      ))}
    </List>
  );
};

export default Navigation;
